import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Mail, Phone, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyClient = {
  name: "", email: "", phone: "",
  capacity_min: "", capacity_max: "",
  engine_type: "any", mast_stages: "",
  tire_type: "any", drive: "any",
  condition: "any", budget_max: "", notes: "",
};

export default function AdminRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [form, setForm] = useState({ ...emptyClient });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/customer-requests`, {
        params: { status: statusFilter },
        withCredentials: true,
      });
      setRequests(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleChange = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const openAddClient = () => {
    setEditingRequest(null);
    setForm({ ...emptyClient });
    setDialogOpen(true);
  };

  const openEditClient = (r) => {
    setEditingRequest(r);
    setForm({
      name: r.name || "",
      email: r.email || "",
      phone: r.phone || "",
      capacity_min: r.capacity_min != null ? String(r.capacity_min) : "",
      capacity_max: r.capacity_max != null ? String(r.capacity_max) : "",
      engine_type: r.engine_type || "any",
      mast_stages: r.mast_stages != null ? String(r.mast_stages) : "",
      tire_type: r.tire_type || "any",
      drive: r.drive || "any",
      condition: r.condition || "any",
      budget_max: r.budget_max != null ? String(r.budget_max) : "",
      notes: r.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name,
        email: form.email || `${form.name.toLowerCase().replace(/\s+/g, ".")}@noemail.local`,
        phone: form.phone,
        capacity_min: form.capacity_min ? parseInt(form.capacity_min) : null,
        capacity_max: form.capacity_max ? parseInt(form.capacity_max) : null,
        engine_type: form.engine_type !== "any" ? form.engine_type : null,
        mast_stages: form.mast_stages ? parseInt(form.mast_stages) : null,
        tire_type: form.tire_type !== "any" ? form.tire_type : null,
        drive: form.drive !== "any" ? form.drive : null,
        condition: form.condition !== "any" ? form.condition : null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
        notes: form.notes,
      };
      if (editingRequest) {
        await axios.put(`${API}/admin/customer-requests/${editingRequest.id}`, body, { withCredentials: true });
        toast.success("Client updated");
      } else {
        const { data } = await axios.post(`${API}/customer-requests`, body);
        toast.success(`Client added — ${data.match_count} match(es) found`);
      }
      setDialogOpen(false);
      setForm({ ...emptyClient });
      fetchRequests();
    } catch (err) {
      toast.error("Error saving client request.");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/customer-requests/${requestId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status changed to ${newStatus}`);
      fetchRequests();
    } catch (err) {
      toast.error("Error updating status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/customer-requests/${id}`, { withCredentials: true });
      toast.success("Client request deleted");
      setDeleteTarget(null);
      fetchRequests();
    } catch (err) {
      toast.error("Error deleting request");
    }
  };

  const statusColor = (s) => {
    if (s === "active") return "bg-yellow-100 text-yellow-800 border-none hover:bg-yellow-100";
    if (s === "matched") return "bg-green-100 text-green-800 border-none hover:bg-green-100";
    if (s === "closed") return "bg-gray-100 text-gray-500 border-none hover:bg-gray-100";
    return "bg-gray-100 text-gray-700 border-none hover:bg-gray-100";
  };

  return (
    <div data-testid="admin-requests" className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Customer Requests</h1>
          <p className="text-sm text-gray-500 mt-1">{requests.length} requests</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-40">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="request-status-filter">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="matched">Matched</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button
            data-testid="add-client-btn"
            onClick={openAddClient}
            className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-4 py-2 rounded-sm text-sm"
          >
            <Plus className="w-4 h-4" /> Add Client
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold">Customer</TableHead>
              <TableHead className="font-semibold">Contact</TableHead>
              <TableHead className="font-semibold">Criteria</TableHead>
              <TableHead className="font-semibold">Budget</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Date</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">Loading...</TableCell>
              </TableRow>
            ) : requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  No customer requests yet.
                </TableCell>
              </TableRow>
            ) : (
              requests.map((r) => (
                <TableRow key={r.id} data-testid={`request-row-${r.id}`}>
                  <TableCell>
                    <button onClick={() => navigate(`/admin/requests/${r.id}`)} className="text-left hover:text-yellow-700 transition-colors">
                      <p className="font-medium text-gray-900">{r.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{r.id.slice(0, 8)}...</p>
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 text-xs text-gray-600">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {r.email}</span>
                      {r.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {r.phone}</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.condition && <Badge variant="outline" className="text-xs">{r.condition}</Badge>}
                      {r.engine_type && <Badge variant="outline" className="text-xs">{r.engine_type}</Badge>}
                      {r.mast_stages && <Badge variant="outline" className="text-xs">{r.mast_stages}stg</Badge>}
                      {r.tire_type && <Badge variant="outline" className="text-xs">{r.tire_type}</Badge>}
                      {r.drive && <Badge variant="outline" className="text-xs">{r.drive}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {r.budget_max ? `$${r.budget_max.toLocaleString()}` : "—"}
                  </TableCell>
                  <TableCell>
                    <Select value={r.status} onValueChange={(v) => handleStatusChange(r.id, v)}>
                      <SelectTrigger className="h-7 w-24 text-xs" data-testid={`status-select-${r.id}`}>
                        <Badge className={`${statusColor(r.status)} capitalize text-xs`}>{r.status}</Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="matched">Matched</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-xs text-gray-500">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`edit-request-${r.id}`}
                        onClick={() => openEditClient(r)}
                        className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-sm"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-request-${r.id}`}
                        onClick={() => setDeleteTarget(r)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add / Edit Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRequest ? "Edit Client" : "Add Client Request"}</DialogTitle>
            <DialogDescription>
              {editingRequest ? "Update the client's details and criteria." : "Manually enter a client's details. The system will check for matching inventory."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            <div className="col-span-2 md:col-span-3">
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Client Name *</label>
              <Input data-testid="client-name" value={form.name} onChange={(e) => handleChange("name", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Email *</label>
              <Input data-testid="client-email" type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Phone</label>
              <Input data-testid="client-phone" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} placeholder="+1..." />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Condition</label>
              <Select value={form.condition} onValueChange={(v) => handleChange("condition", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="used">Used</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Engine Type</label>
              <Select value={form.engine_type} onValueChange={(v) => handleChange("engine_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="propane">Propane</SelectItem>
                  <SelectItem value="diesel">Diesel</SelectItem>
                  <SelectItem value="electric">Electric</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Mast Stages</label>
              <Select value={form.mast_stages || "any"} onValueChange={(v) => handleChange("mast_stages", v === "any" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="2">2 Stages</SelectItem>
                  <SelectItem value="3">3 Stages</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Tire Type</label>
              <Select value={form.tire_type} onValueChange={(v) => handleChange("tire_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="pneumatic">Pneumatic</SelectItem>
                  <SelectItem value="cushion">Cushion</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Drive</label>
              <Select value={form.drive} onValueChange={(v) => handleChange("drive", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="2wd">2WD</SelectItem>
                  <SelectItem value="4wd">4WD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Budget Max ($)</label>
              <Input data-testid="client-budget" type="number" value={form.budget_max} onChange={(e) => handleChange("budget_max", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Capacity Min (lbs)</label>
              <Input type="number" value={form.capacity_min} onChange={(e) => handleChange("capacity_min", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Capacity Max (lbs)</label>
              <Input type="number" value={form.capacity_max} onChange={(e) => handleChange("capacity_max", e.target.value)} />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="text-sm font-semibold text-gray-700 mb-1 block">Notes</label>
              <Textarea value={form.notes} onChange={(e) => handleChange("notes", e.target.value)} placeholder="Any additional details..." />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-sm border border-gray-200"
            >
              Cancel
            </button>
            <button
              data-testid="client-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-bold bg-yellow-400 text-gray-900 hover:bg-yellow-500 rounded-sm disabled:opacity-50"
            >
              {saving ? "Saving..." : editingRequest ? "Update Client" : "Add Client & Check Matches"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client Request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the request from <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
